import type { DeepReadonly, Ref } from 'vue'
import { computed, reactive, readonly, toRef, watch, ref } from 'vue'
import type { User } from '@/entities/user'
import type { UseDeck } from '@/composables/useDeck'
import { UseDeckAuthError, UseDeckPasswordRequiredError, UseDeckPermissionsError, useDeck } from '@/composables/useDeck'
import type { Deck, IncludeCompany, IncludeSiteCamera, NonThreeDSlide, Slide, ThreeDSlide } from '@/entities/deck'
import type { Company } from '@/entities/company'
import { prop, sortBy, uniqBy, zipObj } from 'ramda'
import { useSiteSlide } from '@/composables/useSiteSlide'
import type { CameraData, IncludeCameras, Model3D } from '@/entities/model3d'
import { isThreeDSlide } from '@/lib/type-utils'
import generateDuplicateName from '@/lib/generate-duplicate-name'
import type { UseThreeDViewer } from './useThreeDViewer'
import { ConfirmationDeclinedError } from './useThreeDRepo'
import type { GlobalNotification } from './useGlobalNotification'

export {
  UseDeckAuthError as UseEditDeckAuthError,
  UseDeckPasswordRequiredError as UseEditDeckPasswordRequiredError,
  UseDeckPermissionsError as UseEditDeckPermissionsError,
}

export type EditDeck = DeepReadonly<Omit<Deck, 'slides'> & IncludeCompany>

export interface UseEditDeck extends Omit<UseDeck, 'deck'> {
  deck: Ref<EditDeck | null>
  slides: DeepReadonly<Ref<EditorSlide[]>>
  saving: Readonly<Ref<boolean>>
  cloning: Readonly<Ref<boolean>>
  updateDeck(deck: Partial<Deck>): Promise<void>
  deleteDeck(): Promise<void>
  updateSlide(slide: Partial<Slide>): Promise<void>
  updateCaption(params: { slideId: number; newCaption: string }): Promise<void>
  bulkUpdateSlides(slides: Partial<Slide>[]): Promise<void>
  deleteSlides(slide: Slide[]): Promise<void>
  duplicateDeck(payload: DuplicateDeckPayload, options: DuplicateDeckOptions): Promise<void>
  duplicateSlides(slides: Slide[]): Promise<Slide[]>
  exportSlides(deck: Deck, slides: Slide[], getSlide?: (newSlide: SlideToDuplicate, slide: Slide) => SlideToDuplicate): Promise<void>
  exportAndDuplicateSlides(deck: Deck, slides: Slide[]): Promise<void>
  generateHeaderSlide(slide: Partial<Slide>): Promise<void>
  reorderSlides(slides: Pick<Slide, 'id' | 'order'>[]): Promise<void>
  insertSlides(slides: Array<Partial<Slide>> | Partial<Slide>, insertionIndex: number): Promise<Slide[] | undefined>
  newlyCreatedSlideId: Readonly<Ref<number | null>>
  updateNewlyCreatedSlideId(id: number | null): void
  saveThreeDSlide: (slide: { id: number }, title: string) => Promise<void>
  saveAsThreeDSlide: (slide: Slide, title: string) => Promise<number | void>
}

type ThreeDSlideToDuplicate = Partial<ThreeDSlide> & Pick<Slide, 'id' | 'deck_id' | 'order'>
type NonThreeDSlideToDuplicate = Partial<NonThreeDSlide> & Pick<Slide, 'id' | 'deck_id' | 'order'>
type SlideToDuplicate = ThreeDSlideToDuplicate | NonThreeDSlideToDuplicate

interface CloneCameraOption {
  cloneCamera?: boolean
}

export type ThreeDSlideWithOptions = ThreeDSlide & CloneCameraOption
export type SlideWithOptions = ThreeDSlideWithOptions | NonThreeDSlide

interface DuplicateDeckPayload {
  title: string
  deckId: number
}

export type EditorSlide = Slide & { created?: boolean }

interface DuplicateDeckOptions {
  company?: Company
  duplicateModel: boolean
  duplicateDrillData: boolean
}

export class ClonedSceneToWrongModelError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ClonedSceneToWrongModelError'
  }
}

export function useEditDeck({
  id,
  user,
  initialSlideId,
  onCurrentSlideChange,
  notify,
  onError,
  leaveEditScene,
  threeDViewer,
  setSelectedSlideIds,
  isSavingAsScene,
}: {
  id: Ref<string>
  user: Ref<DeepReadonly<User | undefined>>
  threeDViewer: UseThreeDViewer
  initialSlideId?: Ref<number | null>
  onCurrentSlideChange?(slide: Slide): void
  notify(notificationOpts: GlobalNotification): void
  onError(e: unknown): void
  leaveEditScene: () => void // This shouldn't really be a dependency of useEditDeck. Need to rethink the implementation
  setSelectedSlideIds: (slideIds: number[]) => void // This shouldn't really be a dependency of useEditDeck. Need to rethink the implementation
  isSavingAsScene: Ref<boolean> // Should be part of the heavier refactoring that will give some organization to the amount of interim states
}): UseEditDeck {
  const state = reactive({ saving: false })
  const router = useRouter()

  const useDeckInterface = useDeck({
    id,
    user,
    initialSlideId,
    onCurrentSlideChange,
    include: ['company', 'site_image', 'model_camera', 'assets', 'inactive_meeting_slides'],
    requiresEdit: true,
  })

  const useSiteSlideInterface = useSiteSlide()

  // Currently this is only used for focusing on mount. Technically there can
  // be multiple created slide ids. In those cases we don't focus on mount. If
  // we need to expand this logic then we can add that additional complexity
  // here.
  const newlyCreatedSlideId = ref<number | null>(null)
  const slides = computed<DeepReadonly<EditorSlide>[]>(() =>
    (useDeckInterface.deck.value?.slides ?? []).map(s => ({ ...s, created: newlyCreatedSlideId.value === s.id })),
  )

  const cloning = computed(() => useDeckInterface.deck.value?.status === 'cloning')

  const assertReady = () => {
    newlyCreatedSlideId.value = null
    if (useDeckInterface.deck.value?.status === 'cloning') {
      throw new Error('This action is not permitted.')
    }
  }

  async function _refreshDeck(options?: { signal?: AbortSignal }) {
    try {
      // Refetch the slide list
      await useDeckInterface.refreshDeck(options)
      newlyCreatedSlideId.value = null
    } catch (e) {
      onError(e)
    }
  }

  async function _refreshSlides() {
    try {
      // Refetch the slide list
      await useDeckInterface.refreshSlides()
      newlyCreatedSlideId.value = null
    } catch (e) {
      onError(e)
    }
  }

  const deleteSlides = async (slides: Slide[]): Promise<void> => {
    const currentSlideIndex = useDeckInterface.currentSlideIndex.value
    const includesCurrentSlide =
      currentSlideIndex === useDeckInterface.deck.value?.slides.findIndex(s => slides.some(slide => slide.id === s.id))

    try {
      assertReady()
      state.saving = true

      // Delete slide
      const slidesToDelete = slides.map(slide => ({ id: slide.id }))

      await Promise.all([
        // slides
        $fetch<Slide[]>(`v2/slides`, { method: 'DELETE', body: slidesToDelete, ...useFetchSharedOptions() }),
      ])

      notify({ title: slides.length === 1 ? 'Slide Deleted' : `${slides.length} Slides Deleted` })
    } catch (e) {
      onError(e)
    } finally {
      // Adjust current slide if necessary
      if (includesCurrentSlide)
        useDeckInterface.setCurrentSlideIndex(Math.max(useDeckInterface.firstValidSlide.value, currentSlideIndex - 1))

      await _refreshSlides()
      state.saving = false
    }
  }

  const generateHeaderSlide = async (slide: Partial<Slide>) => {
    if (!slide.title) return

    try {
      assertReady()
      state.saving = true
      await $fetch<Slide[]>(`v2/decks/${slide.deck_id}/slides`, {
        method: 'POST',
        body: {
          title: slide.title,
          order: slide.order,
        },
        ...useFetchSharedOptions(),
      })
    } catch (e) {
      onError(e)
    } finally {
      await _refreshDeck()
      state.saving = false
    }
  }

  // curried ramda function
  const reorder = sortBy(prop('order'))

  const duplicateSlides = async (slides: Slide[]) => {
    let newSlides: Slide[] = []
    let sortedSlides = reorder(slides)

    try {
      assertReady()
      state.saving = true

      const maxOrder = Math.max(...slides.map(slide => slide.order))
      const slidesToCreate = sortedSlides.map((slide, i) => ({
        id: slide.id,
        deck_id: slide.deck_id,
        title: _truncate(slide.title, 255),
        order: maxOrder + i + 1,
        description: null, // don't copy captions from original slide
      }))

      newSlides = await $fetch<Slide[]>(`v2/slides/clone`, { method: 'POST', body: slidesToCreate, ...useFetchSharedOptions() })
      notify({ title: slides.length === 1 ? 'Slide Duplicated' : `${slides.length} Slides Duplicated` })
    } catch (e) {
      if (e instanceof ClonedSceneToWrongModelError) {
        onError(e.message)
      } else {
        onError(e)
      }
    } finally {
      await _refreshDeck()
      state.saving = false
    }

    return newSlides
  }

  let reorderSlidesAbortController: AbortController = new AbortController()

  const reorderSlides = async (slides: Pick<Slide, 'id' | 'order'>[]) => {
    const oldSignal = reorderSlidesAbortController.signal
    try {
      assertReady()

      reorderSlidesAbortController?.abort()
      reorderSlidesAbortController = new AbortController()

      state.saving = true
      await $fetch<Slide[]>(`v2/decks/${id.value}/slides`, {
        method: 'PATCH',
        body: slides.map(({ id, order }) => ({ id, order })),
        ...useFetchSharedOptions(),
        signal: reorderSlidesAbortController.signal,
      })
      await _refreshDeck({ signal: reorderSlidesAbortController.signal })
    } catch (e) {
      if (oldSignal) {
        return
      }

      throw e
    } finally {
      state.saving = false
    }
  }

  const updateDeck = async (deck: Partial<Deck>) => {
    try {
      assertReady()
      state.saving = true
      await $fetch<Deck>(`v2/decks/${deck.id}`, { method: 'PATCH', body: deck, ...useFetchSharedOptions() })
      notify({ title: 'Deck Updated' })
    } catch (e) {
      onError(e)
    } finally {
      await _refreshDeck()
      state.saving = false
    }
  }

  const deleteDeck = async () => {
    try {
      assertReady()
      state.saving = true
      await $fetch<void>(`v2/decks/${id.value}`, { method: 'DELETE', ...useFetchSharedOptions() })
      window.location.href = `/admin/decks`
    } catch (e) {
      onError(e)
    } finally {
      await _refreshDeck()
      state.saving = false
    }
  }

  const updateSlide = async (slide: Partial<Slide & IncludeSiteCamera>): Promise<void> => {
    if (!slide.id) return

    const slideTitle = slide.title?.trim()

    const oldSlide = useDeckInterface.deck.value?.slides.find(s => s.id === slide.id)!
    if (oldSlide.title === slideTitle) {
      // title is unchanged, remove key
      delete slide.title
    }

    if (Object.keys(slide).length === 1) {
      // only id, no change
      return
    }

    try {
      assertReady()
      state.saving = true

      await useSiteSlideInterface.updateSlide(oldSlide, slide)

      const res = await $fetch<Slide>(`v2/slides/${slide.id}`, {
        method: 'PATCH',
        body: {
          ...slide,
          title: slideTitle || undefined, // empty string will trigger no change
        },
        ...useFetchSharedOptions(),
      })

      if (res.type === 'M' && res.camera_id) {
        await threeDViewer.threeDRepo?.updateSceneConfigTitle({
          modelId: res.model_id,
          sceneConfigId: res.camera_id,
          title: res.title,
        })
      }

      notify({ title: 'Slide Updated' })
    } catch (e) {
      onError(e)
    } finally {
      await _refreshDeck()
      state.saving = false
    }
  }

  const updateCaption = async ({ slideId, newCaption }: { slideId: number; newCaption: string }) => {
    try {
      state.saving = true
      await $fetch<Slide>(`v2/slides/${slideId}`, {
        method: 'PATCH',
        body: { description: newCaption },
        ...useFetchSharedOptions(),
      })

      await useDeckInterface.refreshSlides()

      notify({ title: 'Caption Updated' })
    } catch (e) {
      throw e
    } finally {
      state.saving = false
    }
  }

  const bulkUpdateSlides = async (slides: Partial<Slide>[]): Promise<void> => {
    if (!slides.length) {
      notify({ title: 'No slides were updated' })
      return
    }

    try {
      assertReady()
      state.saving = true

      const data = await $fetch<Slide[]>(`v2/decks/${id.value}/slides`, { method: 'PUT', body: slides, ...useFetchSharedOptions() })

      if (!data.some((slide: Slide) => slide.type !== 'P')) {
        notify({ title: 'PDF Source Replaced', text: `${data.length} slides successfully updated` })
      } else {
        notify({ title: `${data.length} Slides Successfully Updated` })
      }
    } catch (e) {
      onError(e)
    } finally {
      await _refreshDeck()
      state.saving = false
    }
  }

  async function _processCameraClones(slides: SlideWithOptions[]): Promise<Slide[]> {
    // SlidePayload includes the extra property `cloneCamera`
    // to indicate that the camera should be cloned
    const camerasToClone = slides.filter(s => 'cloneCamera' in s && s.cloneCamera && s.camera_id)

    if (!camerasToClone.length) return slides

    if (new Set(camerasToClone.map(s => s.model_id)).size !== 1) {
      throw new ClonedSceneToWrongModelError('All cloned scenes must be part of the same model')
    }

    const clonedCamerasMap = await _cloneCameras(camerasToClone)

    return slides.map(slide =>
      isThreeDSlide(slide)
        ? {
            ...slide,
            camera_id: slide.cloneCamera && slide.camera_id ? clonedCamerasMap[slide.camera_id]?.id : slide.camera_id,
          }
        : slide,
    )
  }

  async function _cloneCameras(camerasToClone: SlideWithOptions[]): Promise<Record<number, CameraData>> {
    const getCameraId = prop('camera_id')
    const camerasToCloneIds = uniqBy(getCameraId, camerasToClone).filter(getCameraId) as ThreeDSlideWithOptions[]

    if (!camerasToCloneIds.length) return {}

    const cloned = await $fetch<CameraData[]>(`v2/3dmodel/${camerasToClone[0].model_id}/cameras/clone`, {
      method: 'POST',
      body: camerasToCloneIds.map(({ camera_id, model_camera }) => ({
        id: camera_id,
        title: model_camera && generateDuplicateName(model_camera.title),
      })),
      ...useFetchSharedOptions(),
    })

    return zipObj(camerasToCloneIds.map(getCameraId) as number[], cloned)
  }

  const insertSlides = async (slides: SlideWithOptions | SlideWithOptions[], insertionIndex: number): Promise<Slide[] | undefined> => {
    // support single slide payloads
    if (!Array.isArray(slides)) return insertSlides([slides], insertionIndex)
    if (slides.some(s => s.deck_id !== parseInt(id.value, 10))) {
      onError(new Error('All slides must be part of the same deck'))
      return
    }

    try {
      assertReady()
      state.saving = true
      notify({ title: `Creating slide${slides.length > 1 ? 's' : ''}...` })

      const slidesCamerasCloned = await _processCameraClones(slides)
      const slidesSiteCamerasAdded = await useSiteSlideInterface.insertSlides(slidesCamerasCloned)

      const result = await $fetch<Slide[]>(`v2/decks/${id.value}/slides`, {
        method: 'POST',
        body: slidesSiteCamerasAdded.map((slide, i) => ({
          ...slide,
          order: insertionIndex + i,
        })),
        ...useFetchSharedOptions(),
      })

      if (result.length === 1) {
        newlyCreatedSlideId.value = result[0].id
      }

      // Refetch the slide list
      await useDeckInterface.refreshSlides()
      useDeckInterface.setCurrentSlideIndex(insertionIndex)

      if (slides.length > 1) {
        notify({ title: `${slides.length} Slides Added` })
      } else {
        notify({ title: `Slide Added` })
      }

      return result
    } catch (e) {
      onError(e)
    } finally {
      state.saving = false
    }
  }

  async function exportAndDuplicateSlides(deck: Deck, slides: Slide[]) {
    try {
      const [resultsMap, camerasMap] = await _getClonedModelsAndCameras(slides)

      await exportSlides(deck, slides, (newSlide: SlideToDuplicate, slide: Slide): SlideToDuplicate => {
        if (!isThreeDSlide(slide)) return newSlide

        const model = resultsMap[slide.model_id]
        const oldCamera = camerasMap[slide.model_id].find(cam => cam.id === slide.camera_id)

        return {
          ...newSlide,
          model_id: model.id,
          camera_id: oldCamera ? model.cameras.find(cam => cam.camera_order === oldCamera.camera_order)?.id : null,
        } as ThreeDSlideToDuplicate
      })
    } catch (e) {
      onError(e)
    }
  }

  async function duplicateDeck(payload: DuplicateDeckPayload, options: DuplicateDeckOptions) {
    const { deckId, title } = payload
    const params = new URLSearchParams()
    if (options.duplicateModel) params.append('include', '3dmodel')
    if (options.duplicateDrillData) params.append('include', 'drill')
    const queryString = params.toString() ? `?${params.toString()}` : ''

    try {
      await $fetch<Deck>(`v2/decks/${deckId}/clone${queryString}`, {
        method: 'POST',
        body: {
          title: title,
          company_id: options.company?.id ?? undefined,
        },
        ...useFetchSharedOptions(),
      })

      notify({
        title: '3D Presentation Duplicated',
        text: 'Your 3D Presentation is being duplicated. This can take a few minutes. Once it’s finished you will be able to find it in your 3D Presentation list.',
        action: options.company
          ? undefined
          : {
              icon: 'ExternalLinkIcon',
              name: `Go to 3D Presentations`,
              onClick: () => window.open(`/admin/decks`, '_blank')?.focus(),
            },
      })
    } catch (e) {
      onError(e)
    }
  }

  async function _getClonedModelsAndCameras(
    slides: Slide[],
  ): Promise<[Record<number, Model3D & IncludeCameras>, Record<number, CameraData[]>]> {
    let resultsMap: Record<number, Model3D & IncludeCameras> = []
    let camerasMap: Record<number, CameraData[]> = []

    const uniqueModelIds = Array.from(new Set(slides.map(s => s.model_id as number).filter(x => !!x)))
    // Batch API requests, and map the results back to the ID used for each
    const makeMap = <T>(entries: Promise<readonly [number, T]>[]) => Promise.all(entries).then(x => Object.fromEntries(x))

    ;[resultsMap, camerasMap] = await Promise.all([
      makeMap<Model3D & IncludeCameras>(
        uniqueModelIds.map(id =>
          $fetch<Model3D>(`v2/3dmodel/${id}/clone`, { method: 'POST', ...useFetchSharedOptions() }).then(result => [id, result] as const),
        ),
      ),
      makeMap<CameraData[]>(
        uniqueModelIds.map(id =>
          $fetch<CameraData[]>(`v2/3dmodel/${id}/cameras`, useFetchSharedOptions()).then(result => [id, result] as const),
        ),
      ),
    ])

    return [resultsMap, camerasMap]
  }

  async function exportSlides(
    deck: Deck,
    slides: Slide[],
    getSlide: (newSlide: SlideToDuplicate, slide: Slide) => SlideToDuplicate = slide => slide,
  ) {
    const sortedSlides = reorder(slides)

    try {
      assertReady()
      const { count: latestSlideCount } = await $fetch<{ count: number }>(`v2/decks/${deck.id}/slides`, useFetchSharedOptions())

      const slidesToClone = sortedSlides.map<{ id: number; deck_id: number; order: number }>((slide, index) => {
        const newSlide: SlideToDuplicate = {
          id: slide.id,
          deck_id: deck.id,
          order: latestSlideCount + index,
        }

        return getSlide(newSlide, slide)
      })

      await $fetch<Slide[]>(`v2/slides/clone`, { method: 'POST', body: slidesToClone, ...useFetchSharedOptions() })

      notify({
        title: 'Slide Exported',
        text: `Slide successfully duplicated to ${deck.title}`,
        action: {
          icon: 'ExternalLinkIcon',
          name: `Open ${deck.title}`,
          onClick: () => {
            const routeData = router.resolve({ name: 'editor-decks-id', params: { id: deck.id } })
            window.open(routeData.href, '_blank')
          },
        },
      })
    } catch (e) {
      onError(e)
    } finally {
      await _refreshDeck()
    }
  }

  function updateNewlyCreatedSlideId(id: number | null) {
    newlyCreatedSlideId.value = id
  }

  async function saveThreeDSlide(slide: { id: number }, title: string) {
    if (!slide) return

    try {
      isSavingAsScene.value = true
      await threeDViewer.threeDRepo?.save({ title })
      await $fetch<Slide>(`v2/slides/${slide.id}`, { ...useFetchSharedOptions(), method: 'PATCH', body: { title } })
      notify({ title: '3D Scene Updated' })
      // TODO: This shouldn't really be a depencency
      leaveEditScene()
      await useDeckInterface.refreshSlides()
    } catch (e) {
      if (e instanceof ConfirmationDeclinedError) return
      onError(e)
    } finally {
      isSavingAsScene.value = false
    }
  }

  async function saveAsThreeDSlide(slide: Slide, title: string): Promise<number | void> {
    if (!isThreeDSlide(slide)) return

    try {
      // TODO: This shouldn't really be a depencency
      isSavingAsScene.value = true

      const cameraId = await threeDViewer.threeDRepo?.saveAs({ title })
      notify({ title: '3D Scene Created' })

      if (!cameraId) return

      const data = await insertSlides({ ...slide, camera_id: cameraId, title }, slide.order + 1)
      const newlyCreatedSlideId = data?.[0].id

      if (!newlyCreatedSlideId) return

      await useDeckInterface.refreshSlides()

      // TODO: This shouldn't really be a depencency
      setSelectedSlideIds([newlyCreatedSlideId])

      updateNewlyCreatedSlideId(newlyCreatedSlideId)

      return newlyCreatedSlideId
    } catch (e) {
      if (e instanceof ConfirmationDeclinedError) return
      onError(e)
    } finally {
      isSavingAsScene.value = false
    }
  }

  watch(
    () => useDeckInterface.error.value,
    error => {
      onError(error)
    },
  )

  return {
    ...useDeckInterface,
    // Redeclared deck with slides removed since we modify slides and don't
    // want to create confusion with slides being in deck as well as in slides
    deck: readonly(
      computed(() => {
        if (!useDeckInterface.deck.value) {
          return null
        }

        return {
          ...useDeckInterface.deck.value,
          slides: undefined,
        }
      }),
    ),
    slides: readonly(slides),
    saving: readonly(toRef(state, 'saving')),
    cloning,
    updateDeck,
    deleteDeck,
    deleteSlides,
    generateHeaderSlide,
    duplicateDeck,
    duplicateSlides,
    exportSlides,
    exportAndDuplicateSlides,
    reorderSlides,
    updateSlide,
    updateCaption,
    bulkUpdateSlides,
    insertSlides,
    newlyCreatedSlideId: readonly(newlyCreatedSlideId),
    updateNewlyCreatedSlideId,
    saveThreeDSlide,
    saveAsThreeDSlide,
  }
}

function _truncate(str: string, length = str.length, delimiter: string = '\u2026'): string {
  return str.length <= length ? str : str.slice(0, length - delimiter.length).concat(delimiter)
}
